import React, { useEffect, useState } from "react";
import {
  Modal,
  DialogTitle,
  DialogContent,
  DialogActions,
  Text,
  Pill,
  Flex,
  Icon,
  PillWrapper
} from "@theorgchartdev/storybook";
import { appstr } from "@coreapp/utils/appstr";
import CentralizedModalContainer from "@/appComponents/Blocks/Utils/CentralizedModalContainer/CentralizedModalContainer";
import { QObjDBObject } from "@coreapp/CoreApp/QObjDBObject";
import { PositionText } from "@/appComponents/Blocks/Modals/RecordEditorModal/Components/PositionText";
import { GetWSMgr } from "@/Utils/StateLib";
import { sanitizeBlockedValue } from "@coreapp/utils/FormatFieldValue";
import {
  AssignmentStatus,
  enumObjClassNames,
  enumObjectTypes,
  kEmployeeAssignmentStatusID,
  kEmployeePositionID
} from "@coreapp/constants";
import { FindObject } from "@/appComponents/Blocks/Modals/RecordEditorModal/PositionEditorUtils";

interface ConflictAssignmentModalProps {
  show: boolean;
  conflicts: QObjDBObject[];
  targetPosition: QObjDBObject;
  onConfirm: () => void;
  onCancel: () => void;
}

interface ConflictDetails {
  employee: QObjDBObject;
  currentPosition?: QObjDBObject;
  isReleased: boolean;
  loading: boolean;
}

export const ConflictAssignmentModal: React.FC<ConflictAssignmentModalProps> = ({
  show,
  conflicts,
  targetPosition,
  onConfirm,
  onCancel
}) => {
  const [conflictDetails, setConflictDetails] = useState<ConflictDetails[]>([]);
  const wsMgr = GetWSMgr();
  const planMgr = wsMgr?.GetPlanMgr();

  if (!show) return null;

  useEffect(() => {
    if (!conflicts.length) {
      return;
    }

    // Initialize conflict details
    const initialDetails: ConflictDetails[] = conflicts.map(employee => ({
      employee,
      currentPosition: undefined,
      isReleased:
        employee.SmartGetFieldValue(kEmployeeAssignmentStatusID) === AssignmentStatus.Released,
      loading: true
    }));

    setConflictDetails(initialDetails);

    // Load position details for each employee
    conflicts.forEach(async (employee, index) => {
      const assignmentStatus = employee.SmartGetFieldValue(kEmployeeAssignmentStatusID);

      if (assignmentStatus === AssignmentStatus.Released) {
        // No need to load position for released employees
        setConflictDetails(prev => {
          const updated = [...prev];
          updated[index].loading = false;
          return updated;
        });
        return;
      }

      const currentPositionId = employee.SmartGetFieldValue(kEmployeePositionID) as string;
      if (!currentPositionId) {
        setConflictDetails(prev => {
          const updated = [...prev];
          updated[index].loading = false;
          return updated;
        });
        return;
      }

      // First try to find the position locally
      let currentPosition = FindObject(currentPositionId, enumObjClassNames.position);

      if (!currentPosition && planMgr) {
        // Try to get from cache
        currentPosition = planMgr.GetCacheBaseRecord(enumObjectTypes.position, currentPositionId);
      }

      if (currentPosition) {
        setConflictDetails(prev => {
          const updated = [...prev];
          updated[index].currentPosition = currentPosition;
          updated[index].loading = false;
          return updated;
        });
      } else if (planMgr) {
        // Load the position from server
        planMgr.LoadRecords(
          {
            positionIDs: [currentPositionId],
            outsidePlan: assignmentStatus === AssignmentStatus.AssignedOutOfScope
          },
          (response: any) => {
            const loadedPosition = planMgr.GetCacheBaseRecord(
              enumObjectTypes.position,
              currentPositionId
            );

            if (loadedPosition) {
              setConflictDetails(prev => {
                const updated = [...prev];
                updated[index].currentPosition = loadedPosition;
                updated[index].loading = false;
                return updated;
              });
            } else {
              setConflictDetails(prev => {
                const updated = [...prev];
                updated[index].loading = false;
                return updated;
              });
            }
          }
        );
      }
    });
  }, [conflicts, planMgr]);

  const getEmployeeName = (employee: QObjDBObject) => {
    return sanitizeBlockedValue(employee.SmartGetFieldValue("Name")) || appstr("Unknown Employee");
  };

  const headerProps = {
    modifier: "grey"
  };

  const footerProps = {
    modifier: "grey"
  };

  return (
    <CentralizedModalContainer show={show}>
      <Modal
        id="conflict-assignment-modal"
        show={show}
        centerScreen={true}
        onClose={onCancel}
        fullWidth={true}
        customStyle={{ padding: 0, width: "600px" }}
      >
        <DialogTitle
          divider={true}
          onClose={onCancel}
          {...headerProps}
          customTitleClass={"oc-grey-header"}
        >
          <span className="ocn-explorer-icon-label">
            <Icon size="medium" name="triangle-exclamation" />
            {appstr("Confirm assignments")}
          </span>
        </DialogTitle>

        <DialogContent dividers={false}>
          <Flex direction="column" gap="16px" padding="16px">
            <Text
              size="medium"
              dynamic={{
                template: appstr("There are number assignment issues"),
                replaceDict: {
                  count: `${conflicts.length}`,
                  text: appstr("assignment state issues")
                },
                formatConfig: { count: { type: "bold" }, text: { type: "bold" } }
              }}
            >
              {appstr("There are number assignment issues", {
                count: conflicts.length,
                text: appstr("assignment state issues")
              })}
            </Text>
            <Flex direction="column" gap="8px" margin="16px 0">
              <ul style={{ listStyle: "disc", paddingLeft: "24px", margin: 0 }}>
                {conflictDetails.map((detail, index) => (
                  <li key={index} style={{ marginBottom: "12px" }}>
                    <Flex alignItems="center" gap="8px">
                      <PillWrapper style={{ flexShrink: 0, maxWidth: "250px" }}>
                        <Pill
                          className="oc-relative"
                          label={getEmployeeName(detail.employee) as any}
                        />
                      </PillWrapper>
                      {detail.loading ? (
                        <Text size="medium" colorModifier="grey">
                          {appstr("Loading...")}
                        </Text>
                      ) : detail.isReleased ? (
                        <Text
                          size="small"
                          dynamic={{
                            template: appstr("is currently status"),
                            replaceDict: { status: "released" },
                            formatConfig: { status: { type: "bold" } }
                          }}
                        >
                          {appstr("is currently status", { status: "released" })}
                        </Text>
                      ) : detail.employee.SmartGetFieldValue(kEmployeeAssignmentStatusID) ===
                        AssignmentStatus.AssignedOutOfScope ? (
                        <PositionText
                          templateStr={
                            detail.currentPosition
                              ? "assigned and out of scope to position"
                              : "assigned and out of scope to"
                          }
                          positionObject={detail.currentPosition}
                          positionTitle=""
                          positionID={detail.employee.SmartGetFieldValue(kEmployeePositionID)}
                          textProps={{ size: "medium" }}
                        />
                      ) : detail.currentPosition ? (
                        <PositionText
                          key={`position-${index}-${detail.currentPosition?.SmartGetFieldValue("PositionID") || "unknown"}`}
                          templateStr="is currently assigned to position"
                          positionObject={detail.currentPosition}
                          positionTitle=""
                          positionID=""
                          textProps={{ size: "medium" }}
                        />
                      ) : detail.employee.SmartGetFieldValue(kEmployeePositionID) ? (
                        <PositionText
                          templateStr="is currently assigned to position id"
                          positionObject={null}
                          positionTitle=""
                          positionID={detail.employee.SmartGetFieldValue(kEmployeePositionID)}
                          textProps={{ size: "medium" }}
                        />
                      ) : (
                        <Text size="medium" colorModifier="grey">
                          {appstr("has an unknown assignment status")}
                        </Text>
                      )}
                    </Flex>
                  </li>
                ))}
              </ul>
            </Flex>
            <PositionText
              templateStr="Are you sure you want to assign them to position"
              positionObject={targetPosition}
              positionTitle=""
              positionID=""
              textProps={{ size: "medium" }}
            />
          </Flex>
        </DialogContent>

        <DialogActions divider={true} {...footerProps} customActionsClass={"oc-grey-header"}>
          <DialogActions.Tertiary onClick={onCancel}>{appstr("Cancel")}</DialogActions.Tertiary>
          <DialogActions.Primary onClick={onConfirm}>{appstr("Assign")}</DialogActions.Primary>
        </DialogActions>
      </Modal>
    </CentralizedModalContainer>
  );
};
